import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DeleteButton from '../../components/DeleteButton';
import { deleteSearch, fetchCompare, fetchCorpus, fetchSearchHistory, ingestCorpusFile, ingestCorpusText, runPanelHunter, startCompare } from '../../services/intelligenceApi';

function searchStatusClass(status) {
  if (status === 'complete') return 'active';
  if (status === 'running' || status === 'pending') return 'info';
  if (status === 'failed') return 'error';
  return 'neutral';
}

function riskPillClass(score) {
  if (score == null) return 'risk-pill-neutral';
  if (score >= 40) return 'risk-pill-high';
  if (score >= 20) return 'risk-pill-medium';
  return 'risk-pill-low';
}

export function IntelligenceHistory() {
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSearchHistory()
      .then((res) => setSearches(res.data.searches || []))
      .catch(() => setSearches([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="main-body">
      <div className="hdp-section-head">
        <div>
          <h1 className="page-title">Search History</h1>
          <p className="hdp-section-lead">Click any search to pivot into enrichment results and relationships.</p>
        </div>
        <Link to="/intelligence/search" className="btn btn-primary btn-sm">New search</Link>
      </div>

      <div className="card hdp-table-card">
        <div className="card-body">
          {loading ? (
            <p className="hdp-table-empty">Loading...</p>
          ) : searches.length === 0 ? (
            <p className="hdp-table-empty">No searches yet.</p>
          ) : (
            <table className="data-table entity-table">
              <thead>
                <tr>
                  <th>Query</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Risk</th>
                  <th>Updated</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {searches.map((item) => {
                  const riskScore = item.risk_score != null ? Math.round(item.risk_score) : null;
                  return (
                    <tr
                      key={item.query_id}
                      className="data-table-row-clickable"
                      onClick={() => navigate(`/intelligence/search/${item.query_id}`)}
                    >
                      <td>
                        <span className="data-table-primary">{item.raw_input}</span>
                      </td>
                      <td>
                        <span className="entity-type-chip">{item.detected_type}</span>
                      </td>
                      <td>
                        <span className={`status-badge ${searchStatusClass(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <span className={`risk-pill ${riskPillClass(riskScore)}`}>
                          {riskScore != null ? riskScore : '—'}
                        </span>
                      </td>
                      <td className="timestamp-cell data-table-muted">
                        {item.updated_at ? new Date(item.updated_at).toLocaleString() : '—'}
                      </td>
                      <td className="data-table-actions">
                        <div
                          className="data-table-actions-inner"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <DeleteButton
                            label="Delete"
                            className="btn btn-sm btn-ghost-danger"
                            confirmMessage={`Delete search "${item.raw_input}"?`}
                            onDelete={async () => {
                              await deleteSearch(item.query_id);
                              setSearches((prev) => prev.filter((row) => row.query_id !== item.query_id));
                            }}
                          />
                          <span className="data-table-chevron" aria-hidden="true">›</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const COMPARE_TYPES = [
  { id: 'ip', label: 'IP address' },
  { id: 'domain', label: 'Domain' },
  { id: 'email', label: 'Email' },
  { id: 'wallet', label: 'Crypto wallet' },
];

function verdictClass(color) {
  if (color === 'red') return 'risk-pill-high';
  if (color === 'orange') return 'risk-pill-medium';
  return 'risk-pill-low';
}

export function IntelligenceCompare() {
  const navigate = useNavigate();
  const [inputA, setInputA] = useState('');
  const [inputB, setInputB] = useState('');
  const [compareType, setCompareType] = useState('ip');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [comparison, setComparison] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!comparison || comparison.ready || !comparison.search_id_a || !comparison.search_id_b) {
      return undefined;
    }
    setPolling(true);
    const timer = setInterval(async () => {
      try {
        const res = await fetchCompare(comparison.search_id_a, comparison.search_id_b);
        setComparison(res.data);
        if (res.data.ready) {
          setPolling(false);
        }
      } catch (err) {
        console.error(err);
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [comparison]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setComparison(null);
    try {
      const res = await startCompare({
        input_a: inputA.trim(),
        input_b: inputB.trim(),
        compare_type: compareType,
      });
      setComparison(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Compare failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="main-body">
      <div className="hdp-section-head">
        <div>
          <h1 className="page-title">Compare Indicators</h1>
          <p className="hdp-section-lead">Side-by-side enrichment overlap for IPs, domains, emails, and wallets.</p>
        </div>
        <Link to="/intelligence/search" className="btn btn-secondary btn-sm">New search</Link>
      </div>

      <div className="card">
        <div className="card-body">
          <form className="intel-compare-form" onSubmit={handleSubmit}>
            <div className="intel-compare-grid">
              <div>
                <label className="form-label">Indicator A</label>
                <input className="form-input" value={inputA} onChange={(e) => setInputA(e.target.value)} placeholder="First indicator" />
              </div>
              <div>
                <label className="form-label">Indicator B</label>
                <input className="form-input" value={inputB} onChange={(e) => setInputB(e.target.value)} placeholder="Second indicator" />
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label className="form-label">Type</label>
              <select className="form-select" value={compareType} onChange={(e) => setCompareType(e.target.value)}>
                {COMPARE_TYPES.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
            </div>
            {error && <div className="hdp-alert error" style={{ marginTop: '12px' }}>{error}</div>}
            <button type="submit" className="btn btn-primary" style={{ marginTop: '16px' }} disabled={submitting || !inputA.trim() || !inputB.trim()}>
              {submitting ? 'Starting…' : 'Compare'}
            </button>
          </form>
        </div>
      </div>

      {comparison && !comparison.ready && (
        <div className="card" style={{ marginTop: '18px' }}>
          <div className="card-body">
            <p style={{ color: 'var(--hdp-accent)' }}>
              {polling ? 'Enriching both indicators — comparison updates automatically…' : 'Waiting for enrichment…'}
            </p>
            <div className="entity-kv-grid" style={{ marginTop: '12px' }}>
              <div><strong>A</strong><div>{comparison.search_a?.raw_input}</div><div className="data-table-muted">{comparison.search_a?.status}</div></div>
              <div><strong>B</strong><div>{comparison.search_b?.raw_input}</div><div className="data-table-muted">{comparison.search_b?.status}</div></div>
            </div>
          </div>
        </div>
      )}

      {comparison?.ready && (
        <>
          <div className="card intel-compare-verdict" style={{ marginTop: '18px' }}>
            <div className="card-body">
              <div className="hdp-section-head" style={{ marginBottom: 0 }}>
                <div>
                  <h2 className="page-title" style={{ fontSize: '1.35rem' }}>{comparison.verdict || 'Comparison complete'}</h2>
                  <p className="hdp-section-lead">{comparison.summary}</p>
                </div>
                <span className={`risk-pill ${verdictClass(comparison.verdict_color)}`}>
                  Score {comparison.similarity_score ?? 0}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(`/intelligence/search/${comparison.search_id_a}`)}>
                  Open A
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(`/intelligence/search/${comparison.search_id_b}`)}>
                  Open B
                </button>
              </div>
            </div>
          </div>

          {(comparison.similarities || []).length > 0 && (
            <div className="card" style={{ marginTop: '18px' }}>
              <div className="card-header"><div className="card-title">Detected overlaps</div></div>
              <div className="card-body">
                {(comparison.similarities || []).map((item, idx) => (
                  <div key={`${item.category}-${idx}`} className="intel-compare-overlap">
                    <strong>{item.category}</strong>
                    <span>{item.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(comparison.sections || []).map((section) => (
            <div className="card" style={{ marginTop: '18px' }} key={section.title}>
              <div className="card-header"><div className="card-title">{section.title}</div></div>
              <div className="card-body" style={{ padding: 0 }}>
                <table className="data-table entity-table">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>A</th>
                      <th>B</th>
                      <th>Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(section.fields || []).map((field) => (
                      <tr key={field.label}>
                        <td>{field.label}</td>
                        <td><code>{String(field.a ?? '—')}</code></td>
                        <td><code>{String(field.b ?? '—')}</code></td>
                        <td>{field.match ? '✓' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export function IntelligenceCorpus() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadCorpus = async (searchQuery = query) => {
    setLoading(true);
    try {
      const res = await fetchCorpus(searchQuery.trim());
      setResults(res.data.results || []);
      setRecent(res.data.recent_documents || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Corpus search failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCorpus('');
  }, []);

  const handleSearch = async (event) => {
    event.preventDefault();
    setError('');
    await loadCorpus(query);
  };

  const handleIngestText = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    try {
      const res = await ingestCorpusText(title, content);
      setMessage(`Ingested "${res.data.document.title}"`);
      setTitle('');
      setContent('');
      await loadCorpus(query);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Ingest failed');
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    setMessage('');
    try {
      const res = await ingestCorpusFile(file);
      setMessage(`Ingested "${res.data.document.title}"`);
      await loadCorpus(query);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      event.target.value = '';
    }
  };

  return (
    <div className="main-body">
      <div className="hdp-section-head">
        <div>
          <h1 className="page-title">Internal Corpus</h1>
          <p className="hdp-section-lead">Search ingested investigation notes and docs; corpus matches also appear during enrichment.</p>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSearch} className="intel-search-form">
            <input
              className="form-input intel-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search corpus…"
            />
            <button type="submit" className="btn btn-primary">Search</button>
          </form>
        </div>
      </div>

      <div className="intel-corpus-grid">
        <div className="card">
          <div className="card-header"><div className="card-title">Ingest text</div></div>
          <div className="card-body">
            <form onSubmit={handleIngestText}>
              <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" />
              <textarea className="form-input" rows={6} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Paste markdown or notes…" style={{ marginTop: '8px' }} />
              <button type="submit" className="btn btn-secondary btn-sm" style={{ marginTop: '8px' }} disabled={!content.trim()}>Ingest</button>
            </form>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><div className="card-title">Upload file</div></div>
          <div className="card-body">
            <input type="file" accept=".md,.txt,.log,.csv,.json,.yaml,.yml,.html,.htm" onChange={handleFile} />
            <p className="hdp-section-lead" style={{ marginTop: '8px' }}>Markdown, text, logs, JSON, YAML, HTML.</p>
          </div>
        </div>
      </div>

      {message && <div className="hdp-alert success" style={{ marginTop: '12px' }}>{message}</div>}
      {error && <div className="hdp-alert error" style={{ marginTop: '12px' }}>{error}</div>}

      <div className="card" style={{ marginTop: '18px' }}>
        <div className="card-header"><div className="card-title">{query ? 'Search results' : 'Recent documents'}</div></div>
        <div className="card-body">
          {loading ? (
            <p className="hdp-table-empty">Loading…</p>
          ) : (query ? results : recent).length === 0 ? (
            <p className="hdp-table-empty">{query ? 'No matches.' : 'No documents ingested yet.'}</p>
          ) : (
            (query ? results : recent).map((doc) => (
              <article key={doc.id} className="intel-corpus-result">
                <h3>{doc.title}</h3>
                {doc.source_path && <p className="data-table-muted">{doc.source_path}</p>}
                {doc.snippet && <p>{doc.snippet}</p>}
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function IntelligencePanelHunter() {
  const [rawUrls, setRawUrls] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const urls = rawUrls.split('\n').map((line) => line.trim()).filter(Boolean);
    if (!urls.length) return;
    setLoading(true);
    setError('');
    try {
      const res = await runPanelHunter(urls);
      setResults(res.data?.data || res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Panel scan failed');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-body">
      <div className="hdp-section-head">
        <div>
          <h1 className="page-title">Panel Hunter</h1>
          <p className="hdp-section-lead">Probe URLs for known scam-panel and stealer C2 fingerprints.</p>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <label className="form-label">URLs or host:port (one per line)</label>
            <textarea
              className="form-input"
              rows={8}
              value={rawUrls}
              onChange={(e) => setRawUrls(e.target.value)}
              placeholder={'https://example-panel.example\n203.0.113.10:8080'}
            />
            {error && <div className="hdp-alert error" style={{ marginTop: '12px' }}>{error}</div>}
            <button type="submit" className="btn btn-primary" style={{ marginTop: '12px' }} disabled={loading || !rawUrls.trim()}>
              {loading ? 'Scanning…' : 'Scan panels'}
            </button>
          </form>
        </div>
      </div>

      {results && (
        <div className="card" style={{ marginTop: '18px' }}>
          <div className="card-header">
            <div className="card-title">
              {results.matched != null ? `${results.matched} match(es) of ${results.scanned} scanned` : 'Scan results'}
            </div>
          </div>
          <div className="card-body">
            {(results.results || []).length === 0 ? (
              <p className="hdp-table-empty">No URLs scanned.</p>
            ) : (
              (results.results || []).map((item) => {
                const top = item.data?.top_match;
                return (
                  <article key={item.url} className="intel-panel-match">
                    <div className="hdp-section-head" style={{ marginBottom: '8px' }}>
                      <strong>{top?.name || 'No match'}</strong>
                      {top?.score != null && <span className="entity-type-chip">Score {top.score}</span>}
                    </div>
                    <p><code>{item.url}</code></p>
                    {item.data?.summary && <p className="data-table-muted">{item.data.summary}</p>}
                    {(item.data?.matches || []).length > 0 && (
                      <p className="data-table-muted">
                        Signatures: {(item.data.matches || []).map((m) => m.name).join(', ')}
                      </p>
                    )}
                  </article>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
